import { useState } from "react";
import { ChevronDown, ChevronRight, RotateCcw } from "lucide-react";

import {
  RARITY_BUCKETS,
  WEIGHT_PROFILES,
  type MixOverride,
  type RarityBucket,
  type WeightProfile,
  type WeightProfileName,
} from "@/features/bundleWeightedPicker";

const PROFILES_IN_UI: WeightProfileName[] = ["bulk", "standard", "premium"];

const PROFILE_LABELS: Record<WeightProfileName, string> = {
  bulk: "Bulk mix",
  standard: "Standard mix",
  premium: "Premium mix",
  pokemonType: "Pokémon type mix",
  trainer: "Trainer mix",
  energy: "Energy mix",
};

const BUCKET_LABELS: Record<RarityBucket, string> = {
  common: "Common",
  uncommon: "Uncommon",
  rare: "Rare",
  holo: "Holo",
  ultraRare: "Ultra Rare",
};

const PROFILE_SUMMARIES: Record<WeightProfileName, string> = {
  bulk: "Default for 100-card filler packs",
  standard: "Default for 50-card themed lots",
  premium: "Default for hit-heavy packs",
  pokemonType: "",
  trainer: "",
  energy: "",
};

interface Props {
  mixOverrides: Partial<Record<WeightProfileName, MixOverride>>;
  onChange: (next: Partial<Record<WeightProfileName, MixOverride>>) => void;
}

/**
 * Rebalances all five weight buckets to sum to 100 after one bucket changes.
 * Excess is taken proportionally from the others by current value; if the rest
 * are all zero, the remainder is spread evenly.
 */
function rebalanceWeights(
  weights: Record<RarityBucket, number>,
  changed: RarityBucket,
  nextValue: number,
): Record<RarityBucket, number> {
  const clamped = Math.max(0, Math.min(100, nextValue));
  const otherKeys = RARITY_BUCKETS.filter((b) => b !== changed);
  const otherSum = otherKeys.reduce((s, k) => s + weights[k], 0);
  const remaining = 100 - clamped;
  if (remaining < 0) return weights;

  const out: Record<RarityBucket, number> = { ...weights, [changed]: clamped };

  if (otherSum === 0) {
    const each = Math.floor(remaining / otherKeys.length);
    for (const k of otherKeys) out[k] = each;
    out[otherKeys[0]] += remaining - each * otherKeys.length;
    return out;
  }

  let allocated = 0;
  for (const k of otherKeys) {
    const share = Math.round((weights[k] / otherSum) * remaining);
    out[k] = share;
    allocated += share;
  }
  const drift = remaining - allocated;
  if (drift !== 0) {
    const biggest = otherKeys.reduce((a, b) => (out[a] >= out[b] ? a : b));
    out[biggest] = Math.max(0, out[biggest] + drift);
  }
  return out;
}

function effectiveProfile(
  name: WeightProfileName,
  override: MixOverride | undefined,
): WeightProfile {
  const base = WEIGHT_PROFILES[name];
  const weights = { ...base.weights };
  if (override?.weights) {
    for (const b of RARITY_BUCKETS) {
      const v = override.weights[b];
      if (typeof v === "number" && Number.isFinite(v) && v >= 0) weights[b] = v;
    }
  }
  return {
    name,
    bundleSize: 100,
    weights,
    minGuarantees: {
      ...base.minGuarantees,
      ...(override?.minGuarantees ?? {}),
    },
    maxDuplicatesPerCard:
      override?.maxDuplicatesPerCard ?? base.maxDuplicatesPerCard,
  };
}

function StepBtn({
  onClick,
  label,
  disabled,
}: {
  onClick: () => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="min-w-[30px] rounded bg-muted px-2 py-1 text-xs font-bold text-foreground disabled:opacity-40"
    >
      {label}
    </button>
  );
}

export function MixOverridesEditor({ mixOverrides, onChange }: Props) {
  const [expanded, setExpanded] = useState<WeightProfileName | null>(null);

  const updateOverride = (name: WeightProfileName, patch: MixOverride) => {
    const merged: MixOverride = { ...(mixOverrides[name] ?? {}), ...patch };
    onChange({ ...mixOverrides, [name]: merged });
  };

  const adjustWeight = (
    name: WeightProfileName,
    bucket: RarityBucket,
    delta: number,
  ) => {
    const current = effectiveProfile(name, mixOverrides[name]).weights;
    const next = rebalanceWeights(current, bucket, current[bucket] + delta);
    updateOverride(name, { weights: next });
  };

  const adjustMinGuarantee = (
    name: WeightProfileName,
    bucket: RarityBucket,
    delta: number,
  ) => {
    const profile = effectiveProfile(name, mixOverrides[name]);
    const next = Math.max(
      0,
      Math.min(20, (profile.minGuarantees[bucket] ?? 0) + delta),
    );
    updateOverride(name, {
      minGuarantees: { ...profile.minGuarantees, [bucket]: next },
    });
  };

  const adjustMaxDup = (name: WeightProfileName, delta: number) => {
    const profile = effectiveProfile(name, mixOverrides[name]);
    const next = Math.max(
      1,
      Math.min(20, profile.maxDuplicatesPerCard + delta),
    );
    updateOverride(name, { maxDuplicatesPerCard: next });
  };

  const resetProfile = (name: WeightProfileName) => {
    const next = { ...mixOverrides };
    delete next[name];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Mystery pack weights
      </div>
      <div className="overflow-hidden rounded-md border border-border bg-card">
        {PROFILES_IN_UI.map((name, idx) => {
          const isExpanded = expanded === name;
          const profile = effectiveProfile(name, mixOverrides[name]);
          const hasOverride = !!mixOverrides[name];
          return (
            <div
              key={name}
              className={
                idx < PROFILES_IN_UI.length - 1 ? "border-b border-border" : ""
              }
            >
              <button
                type="button"
                onClick={() => setExpanded(isExpanded ? null : name)}
                className="flex w-full items-center gap-2 px-3 py-3 text-left"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-foreground">
                    {PROFILE_LABELS[name]}
                    {hasOverride ? " (customized)" : ""}
                  </div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    {PROFILE_SUMMARIES[name]} · C:{profile.weights.common} UC:
                    {profile.weights.uncommon} R:{profile.weights.rare} H:
                    {profile.weights.holo} U:{profile.weights.ultraRare}
                  </div>
                </div>
              </button>

              {isExpanded && (
                <div className="space-y-2 px-3 pb-3">
                  {RARITY_BUCKETS.map((b) => (
                    <div
                      key={b}
                      className="flex items-center justify-between py-1"
                    >
                      <span className="text-[13px] font-medium text-foreground">
                        {BUCKET_LABELS[b]}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <StepBtn
                          onClick={() => adjustWeight(name, b, -5)}
                          label="−5"
                          disabled={profile.weights[b] <= 0}
                        />
                        <span className="min-w-[36px] text-center text-[13px] font-bold text-foreground">
                          {profile.weights[b]}
                        </span>
                        <StepBtn
                          onClick={() => adjustWeight(name, b, 5)}
                          label="+5"
                          disabled={profile.weights[b] >= 100}
                        />
                      </div>
                    </div>
                  ))}

                  <div className="my-1 border-b border-border" />

                  <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Min guarantees
                  </div>
                  {(["rare", "holo", "ultraRare"] as RarityBucket[]).map(
                    (b) => (
                      <div
                        key={b}
                        className="flex items-center justify-between py-1"
                      >
                        <span className="text-[13px] font-medium text-foreground">
                          Min {BUCKET_LABELS[b].toLowerCase()}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <StepBtn
                            onClick={() => adjustMinGuarantee(name, b, -1)}
                            label="−"
                            disabled={(profile.minGuarantees[b] ?? 0) <= 0}
                          />
                          <span className="min-w-[36px] text-center text-[13px] font-bold text-foreground">
                            {profile.minGuarantees[b] ?? 0}
                          </span>
                          <StepBtn
                            onClick={() => adjustMinGuarantee(name, b, 1)}
                            label="+"
                          />
                        </div>
                      </div>
                    ),
                  )}

                  <div className="flex items-center justify-between py-1">
                    <span className="text-[13px] font-medium text-foreground">
                      Max dupes per card
                    </span>
                    <div className="flex items-center gap-1.5">
                      <StepBtn
                        onClick={() => adjustMaxDup(name, -1)}
                        label="−"
                        disabled={profile.maxDuplicatesPerCard <= 1}
                      />
                      <span className="min-w-[36px] text-center text-[13px] font-bold text-foreground">
                        {profile.maxDuplicatesPerCard}
                      </span>
                      <StepBtn
                        onClick={() => adjustMaxDup(name, 1)}
                        label="+"
                      />
                    </div>
                  </div>

                  {hasOverride && (
                    <button
                      type="button"
                      onClick={() => resetProfile(name)}
                      className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md bg-muted py-2 text-xs font-semibold text-muted-foreground hover:opacity-90"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Reset to defaults
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        These weights drive how Studio templates with profile = Bulk / Standard
        / Premium pick cards. Trainer, Energy, and Pokémon-type profiles keep
        their hardcoded defaults.
      </p>
    </div>
  );
}
